import React from 'react'
import { Modal } from 'react-bootstrap'
import { Model } from '../../types/models/model'
import { useFetchModelsQuery } from '../../services/modelsApi'
import { useAppSelector } from '../../store/hooks'
import { selectSelectedProjectId } from '../../store/slices/projectSlice'
import ModelItem from '../styled/ModelSelect/ModelItem'
import styled from 'styled-components'
import { useGetProjectQuery } from '../../services/projectsApi'

const StyledModal = styled(Modal)`
  .modal-content {
    background: #1a1a1a;
    border: none;
  }

  .modal-header {
    background: linear-gradient(45deg, #402563, #114ee0);
    border-bottom: none;
    padding: 1.25rem 1.5rem;

    .modal-title {
      color: white;
      font-weight: 600;
      font-size: 1.25rem;
    }
  }

  .modal-body {
    background: #2a2a2a;
    padding: 1.5rem;
    max-height: 80vh;
    overflow-y: auto;
  }

  .model-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1rem;
  }
`

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  color: #a0a0a0;
  min-height: 200px;

  h4 {
    margin-bottom: 1rem;
    color: #ffffff;
  }

  p {
    max-width: 400px;
    line-height: 1.5;
  }
`

interface SelectModelModalProps {
  show: boolean
  onHide: () => void
  onModelSelect: (model: Model) => void
}

const SelectModelModal: React.FC<SelectModelModalProps> = ({ show, onHide, onModelSelect }) => {
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const { data: modelsData, isLoading } = useFetchModelsQuery(
    { project_id: selectedProjectId || '' },
    {
      skip: !selectedProjectId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  )

  return (
    <StyledModal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Select Model</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <EmptyStateContainer>
            <h4>Loading models...</h4>
          </EmptyStateContainer>
        ) : !modelsData?.models?.length ? (
          <EmptyStateContainer>
            <h4>No Models Available</h4>
            <p>
              There are currently no models in this project. Models will appear here once they have
              been created and trained.
            </p>
          </EmptyStateContainer>
        ) : (
          <div className="model-grid">
            {modelsData?.models.map((model) => (
              <div
                key={model.id}
                onClick={() => onModelSelect(model)}
                style={{ cursor: 'pointer' }}
              >
                <ModelItem model={model} project={project} />
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
    </StyledModal>
  )
}

export default SelectModelModal
